import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import type { AppIcon } from '../ui/FaIcons';
import { cn } from '../../lib/cn';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/Tooltip';

interface NavItemProps {
  icon: AppIcon;
  label: string;
  to: string;
  disabled?: boolean;
  collapsed?: boolean;
  indent?: boolean;
  /** Number badge shown on the right of the label (hidden when collapsed) */
  badgeCount?: number;
  /** If provided, the item renders as a button that triggers this callback instead of a NavLink. */
  onClick?: () => void;
}

export function NavItem({
  icon: Icon,
  label,
  to,
  disabled = false,
  collapsed = false,
  indent = false,
  badgeCount,
  onClick,
}: NavItemProps) {
  const content = (
    <span
      className={cn(
        'flex items-center gap-3 px-5 py-2.5 transition-colors w-full text-sm font-medium',
        disabled
          ? 'opacity-40 cursor-not-allowed text-cime-sidebar-text-muted'
          : 'text-cime-sidebar-text-muted cursor-pointer',
      )}
    >
      <Icon className="w-5 h-5 flex-shrink-0" aria-hidden />
      {!collapsed && (
        <span className="flex-1 truncate">{label}</span>
      )}
      {!collapsed && badgeCount !== undefined && (
        <span className="ml-auto min-w-5 h-5 flex items-center justify-center rounded-full bg-cime-gold text-cime-primary text-xs font-semibold px-1">
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      )}
    </span>
  );

  const withTooltip = (trigger: ReactNode, tooltipText: string) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block w-full">{trigger}</span>
      </TooltipTrigger>
      <TooltipContent side="right">{tooltipText}</TooltipContent>
    </Tooltip>
  );

  if (disabled) {
    const item = (
      <div role="menuitem" aria-disabled="true">
        {content}
      </div>
    );

    if (collapsed) {
      return withTooltip(item, label);
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div role="menuitem" aria-disabled="true" className="block w-full">
            {content}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right">Bientôt disponible</TooltipContent>
      </Tooltip>
    );
  }

  if (onClick) {
    const button = (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex items-center gap-3 transition-colors w-full text-sm font-medium text-left',
          indent ? 'pl-10 pr-5 py-1.5 text-xs' : 'px-5 py-2.5',
          'text-cime-sidebar-text-muted hover:bg-cime-sidebar-active-bg hover:text-cime-sidebar-text',
        )}
      >
        <Icon className="w-5 h-5 flex-shrink-0" aria-hidden />
        {!collapsed && <span className="flex-1 truncate">{label}</span>}
        {!collapsed && badgeCount !== undefined && (
          <span className="ml-auto min-w-5 h-5 flex items-center justify-center rounded-full bg-cime-gold text-cime-primary text-xs font-semibold px-1">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </button>
    );
    return collapsed ? withTooltip(button, label) : button;
  }

  const navItem = (
    <NavLink
      to={to}
      end={to === '/tickets'}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 transition-colors w-full text-sm font-medium',
          indent ? 'pl-10 pr-5 py-1.5 text-xs' : 'px-5 py-2.5',
          isActive
            ? 'bg-cime-sidebar-active-bg text-cime-sidebar-text border-l-2 border-cime-gold pl-[calc(1.25rem-2px)]'
            : 'text-cime-sidebar-text-muted hover:bg-cime-sidebar-active-bg hover:text-cime-sidebar-text',
        )
      }
    >
      <Icon className="w-5 h-5 flex-shrink-0" aria-hidden />
      {!collapsed && <span className="flex-1 truncate">{label}</span>}
      {!collapsed && badgeCount !== undefined && (
        <span className="ml-auto min-w-5 h-5 flex items-center justify-center rounded-full bg-cime-gold text-cime-primary text-xs font-semibold px-1">
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      )}
    </NavLink>
  );

  if (collapsed) {
    return withTooltip(navItem, label);
  }

  return navItem;
}
